import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { makeid } from "../../../utility";

// A stable identity for a file, independent of our generated keys.
// Used to detect the same file being added twice.
function fileSignature(file) {
    return `${file.name}|${file.size}|${file.lastModified}`;
}

export default function DragDropFileBox(props) {
    const { data, dispatch } = props;

    const fileCache = window.fileCache;

    // Shared by the drop and file-picker paths.
    // Dedupes on name + size + lastModified against the current queue
    // (and within the same batch), silently skipping duplicates.
    const addFiles = (files) => {
        const existingSignatures = data.fileList.map((f) => fileSignature(f));

        const newFileMetadata = [];

        files.forEach((file) => {
            const signature = fileSignature(file);

            if (existingSignatures.includes(signature)) {
                // Already queued - skip silently
                return;
            }

            existingSignatures.push(signature);

            const key = makeid(25);

            // Store actual file in cache outside React state
            fileCache.set(key, file);

            newFileMetadata.push({
                key: key,
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
            });
        });

        if (newFileMetadata.length > 0) {
            dispatch({ type: "ADD_FILE_TO_LIST", files: newFileMetadata });
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "SET_DROP_DEPTH", dropDepth: data.dropDepth + 1 });
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Compute the new depth first - data.dropDepth is the pre-decrement
        // value, so checking it directly meant the highlight never cleared
        const newDropDepth = Math.max(data.dropDepth - 1, 0);

        dispatch({ type: "SET_DROP_DEPTH", dropDepth: newDropDepth });

        if (newDropDepth > 0) return;

        dispatch({ type: "SET_IN_DROP_ZONE", inDropZone: false });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        dispatch({ type: "SET_IN_DROP_ZONE", inDropZone: true });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        let files = [...e.dataTransfer.files];
        files = files.filter((f) => f.type !== "");

        addFiles(files);

        e.dataTransfer.clearData();
        dispatch({ type: "SET_DROP_DEPTH", dropDepth: 0 });
        dispatch({ type: "SET_IN_DROP_ZONE", inDropZone: false });
    };

    const handleChoosePhotoClick = (e) => {
        const files = e.target.files;

        if (files && files.length) {
            addFiles(Array.from(files));
        }

        // Reset the input so selecting the same file again (e.g. after
        // removing it from the list) fires onChange
        e.target.value = "";
    };

    return (
        <Paper
            variant="outlined"
            onDrop={(e) => handleDrop(e)}
            onDragOver={(e) => handleDragOver(e)}
            onDragEnter={(e) => handleDragEnter(e)}
            onDragLeave={(e) => handleDragLeave(e)}
            sx={{
                minHeight: { xs: 210, sm: 250 },
                px: { xs: 2, sm: 4 },
                py: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                borderRadius: 3,
                borderWidth: 2,
                borderStyle: "dashed",
                borderColor: data.inDropZone
                    ? "primary.main"
                    : "divider",
                backgroundColor: data.inDropZone
                    ? "rgba(0, 149, 61, 0.08)"
                    : "background.paper",
                boxShadow: (theme) =>
                    data.inDropZone
                        ? "0 8px 28px rgba(0, 149, 61, 0.1)"
                        : theme.palette.mode === "dark"
                          ? "0 4px 18px rgba(0, 0, 0, 0.22)"
                          : "0 4px 18px rgba(20, 35, 25, 0.04)",
                transition:
                    "border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
                transform: data.inDropZone ? "scale(1.005)" : "none",
            }}
        >
            <Box
                sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    color: "primary.main",
                    backgroundColor: "action.hover",
                    mb: 1.75,
                }}
            >
                <CloudUploadOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="h6" component="h3">
                {data.inDropZone
                    ? "Drop files here"
                    : "Choose files to upload"}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, mb: 2, maxWidth: 440 }}
            >
                Drag and drop files into this area, or browse files on your
                device. You can review every file before the upload begins.
            </Typography>
            <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadOutlinedIcon />}
                sx={{ borderRadius: 2, px: 2.5 }}
            >
                Browse files
                <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleChoosePhotoClick}
                />
            </Button>
        </Paper>
    );
}
